<template>
  <div>
    <!-- The photo families see when booking, so show the real thing rather than
         only checking it off in the list below. It takes the full column width:
         the sidebar is a col-md-2, and anything beside the photo wrapped to
         three lines. -->
    <img v-if="hasPhoto(coachData)" :src="coachData.avatar" class="pl-coach-photo" alt="Coach photo" />
    <div v-else class="pl-coach-photo is-placeholder">
      <span class="text-gray-500 font-semi text-30">{{ initials(coachData) }}</span>
    </div>
    <!-- Same principle as the photo: the bio is part of what families see, so
         show the real thing (mirroring the Biography expander on the family
         booking page), not just a checkmark. Sanitized server-side. -->
    <vertical-expander v-if="hasBio(coachData)" class="m-t-sm">
      <template v-slot:title>
        <p class="m-y-none font-semi">Biography</p>
      </template>
      <template v-slot:content>
        <div v-if="coachData.bio" class="private-lesson-bio text-14 text-gray-600 m-t-xs" v-html="coachData.bio"></div>
        <p v-else class="text-14 text-gray-600 m-t-xs m-b-none">{{ coachData.plain_bio }}</p>
      </template>
    </vertical-expander>

    <p class="font-semi m-t-sm m-b-xxs">Private lesson setup</p>
    <span class="pl-badge" :class="status.cssClass" :title="status.title">{{ status.label }}</span>
    <div class="pl-setup-progress m-t-xs">
      <div class="pl-setup-progress-fill" :class="status.cssClass" :style="{ width: `${progressPercent(coachData)}%` }"></div>
    </div>

    <ul class="pl-coach-checklist m-t-sm m-b-none">
      <li :class="{ 'is-done': hasPhoto(coachData) }">
        <i :class="hasPhoto(coachData) ? 'fa-solid fa-circle-check text-green-500' : 'fa-regular fa-circle text-gray-400'"></i>
        {{ hasPhoto(coachData) ? 'Photo added' : 'No photo yet' }}
      </li>
      <li :class="{ 'is-done': hasBio(coachData) }">
        <i :class="hasBio(coachData) ? 'fa-solid fa-circle-check text-green-500' : 'fa-regular fa-circle text-gray-400'"></i>
        {{ hasBio(coachData) ? 'Bio set' : 'No bio set' }}
      </li>
      <li :class="{ 'is-done': hasPricing(coachData) }">
        <i :class="hasPricing(coachData) ? 'fa-solid fa-circle-check text-green-500' : 'fa-regular fa-circle text-gray-400'"></i>
        <span class="pl-checklist-text">{{ pricingText(coachData) }}</span>
      </li>
      <li :class="{ 'is-done': coachData.accepting_private_lessons }">
        <i :class="coachData.accepting_private_lessons ? 'fa-solid fa-circle-check text-green-500' : 'fa-regular fa-circle text-gray-400'"></i>
        {{ coachData.accepting_private_lessons ? 'Accepting private lessons' : 'Not accepting private lessons' }}
      </li>
    </ul>

    <coach-billing-plans
      v-if="billingPlans.length > 0"
      :billing-plans="billingPlans"
      class="m-t-xs"
    ></coach-billing-plans>

    <!-- "Continue setup" is the nudge for the required steps (pricing +
         accepting) — phrased as navigation (with the chevron) rather than
         "Finish", which read as a done/submit button. Once bookable, the
         remaining photo/bio polish shouldn't read as unfinished business —
         the checklist already shows it. -->
    <button type="button" class="btn btn-primary btn-compact w-full m-t-sm" @click="openDrawer">
      {{ isBookable(coachData) ? 'Manage setup' : 'Continue setup' }}
      <i v-if="!isBookable(coachData)" class="fa-solid fa-chevron-right m-l-xxs"></i>
    </button>
    <p v-if="!canEditPricing && !hasPricing(coachData)" class="text-12 text-gray-600 m-t-xs m-b-none">
      <i class="fa-solid fa-circle-xmark text-red-500 m-r-xxs"></i> Ask your admin to set pricing on your behalf
    </p>

    <!-- Public sign-up link, aimed at NEW families without a WrestlingIQ
         account. The audience line matters: current members already book via
         the private lessons tab in the app, so coaches shouldn't hand this
         URL to existing families. Plain span (not a link) because this panel
         also renders inside the native app webview. -->
    <p class="font-semi m-t-sm m-b-xxs">Your new-client link</p>
    <template v-if="shareableLessonsUrl">
      <div class="flex align-items-center gap-x-xs">
        <span class="text-12 text-gray-600 pl-truncate" style="min-width: 0;" :title="shareableLessonsUrl">{{ shareableLessonsUrl }}</span>
        <copy-icon :text="shareableLessonsUrl" class="flex-shrink-0"></copy-icon>
      </div>
      <p class="text-12 text-gray-600 m-t-xxs m-b-none">Share this link with new families who aren't on WrestlingIQ yet. Current members can already book you from the private lessons tab in the app.</p>
    </template>
    <p v-else class="text-12 text-gray-600 m-b-none">Your shareable link for new families goes live once you're accepting private lessons.</p>

    <private-lesson-coach-drawer
      v-if="drawerOpen"
      :coach="coachData"
      :currency="currency"
      :can-edit-pricing="canEditPricing"
      :endpoint="endpoint"
      :access-key="accessKey"
      :cloudfront-base-url="cloudfrontBaseUrl"
      :existing-attachments="coachData.profile_photos || []"
      @close="closeDrawer"
      @saved="onDrawerSaved"
    ></private-lesson-coach-drawer>
  </div>
</template>

<script>
import CoachBillingPlans from './coach_billing_plans.vue';
import CopyIcon from './copy_icon.vue';
import PrivateLessonCoachDrawer from './private_lesson_coach_drawer.vue';
import VerticalExpander from './vertical_expander.vue';
import { coachSetupStatusMix } from './mixins/coach_setup_status_mix';
import { appEmbedMix } from './mixins/app_embed_mix';

export default {
  name: 'private-lesson-coach-panel',
  components: {
    CoachBillingPlans,
    CopyIcon,
    PrivateLessonCoachDrawer,
    VerticalExpander,
  },
  mixins: [coachSetupStatusMix, appEmbedMix],
  props: {
    coach: {
      type: Object,
      required: true,
    },
    canEditPricing: {
      type: Boolean,
      default: false,
    },
    currency: {
      type: String,
      default: 'usd',
    },
    endpoint: {
      type: String,
      required: true,
    },
    accessKey: {
      type: String,
      required: true,
    },
    cloudfrontBaseUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      // Server-rendered coach JSON, replaced in place after a drawer save so
      // the sidebar reflects edits without a page reload.
      coachData: _.cloneDeep(this.coach),
      drawerOpen: false,
    };
  },
  computed: {
    status() {
      return this.setupStatus(this.coachData);
    },
    billingPlans() {
      return this.coachData.private_lesson_billing_plans || [];
    },
    // The public page 404s/blocks booking until the coach is accepting, so
    // only offer the URL for sharing once it actually works for a new family.
    shareableLessonsUrl() {
      return this.coachData.accepting_private_lessons ? this.coachData.lessons_url : null;
    },
  },
  mounted() {
    // In the embed, editing happens on a pushed native screen; when this
    // webview is revealed again on pop, refetch so the checklist isn't stale
    if (this.forAppEmbed) {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  },
  methods: {
    openDrawer() {
      // The embed pushes a dedicated native screen (like availability and
      // event edits) instead of overlaying the drawer inside this webview
      if (this.forAppEmbed) {
        this.askAppToPush(`${window.location.origin}/app_embeds/private_lessons/profile/setup`, 'Edit Profile');
        return;
      }
      this.drawerOpen = true;
    },
    closeDrawer() {
      this.drawerOpen = false;
    },
    onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.refreshCoach();
      }
    },
    refreshCoach() {
      return axios.get(this.$apiService.coachUrl(this.coachData.id))
        .then((response) => {
          this.coachData = response.data;
        });
    },
    onDrawerSaved() {
      this.refreshCoach()
        .then(() => {
          this.closeDrawer();
        })
        .catch(() => {
          // Save succeeded but the refresh failed — reload rather than leave
          // the checklist showing stale state.
          window.location.reload();
        });
    },
  },
};
</script>
